import { FieldValues, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { RiEyeFill, RiEyeOffFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { z } from "zod";
import { Loader } from "../shared";
import ErrorMessage from "../errors/ErrorMessage";
import { LoginFormSchema } from "zod.schema";
import AuthenticationService from "services/Authentication";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCountdownTimer } from "hooks/useCountdownTimer";
import usePasswordVisibility from "hooks/usePasswordVisibility";
import { useNavigate, useNavigation, useLocation } from "react-router-dom";
import { AppDispatch, RootState } from "store";
import { login, loginWithOtp, verifyOtpAndLogin } from "store/reducers/authentication";
import formatTime from "utils/formatTime";
import { NavLink } from "react-router-dom";
import { useEffect } from "react";
import Icon from "assets/icons";
import FrameSupportIcon from "assets/icons/frame_support.svg";
import AttachEmailIcon from "assets/icons/attach_email.svg";
import CallIcon from "assets/icons/call.svg";


export type FormData = z.infer<typeof LoginFormSchema>;

const authService = new AuthenticationService();

const LoginForm = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emailFromParams = queryParams.get("emailid") || "";

  const {
    register,
    handleSubmit,
    setFocus,
    setValue,
    formState: { errors },
    trigger,
    watch,
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: {
      strategy: "password",
    },
    mode: "onChange",
  });

  const strategy = watch("strategy");
  const { timeLeft, isTimerRunning, start } = useCountdownTimer();
  const { showPassword, togglePasswordVisibility } = usePasswordVisibility();
  const router = useNavigate();
  const { isLoading, error } = useSelector((state: RootState) => state.auth);

  const dispatch = useDispatch<AppDispatch>();

  const onSubmit = async (data: FieldValues) => {
    try {
      if (strategy === "password") {
        const response = await dispatch(
          login({ username: data.username, password: data.password })
        ).unwrap();

        if (response.user && response.token) {
          toast.success("Logged In :" + new Date().toDateString());
          router("/dashboard");
        }
      }

      if (strategy === "otp") {
        const response = await dispatch(
          verifyOtpAndLogin({ email: data.username, otp: data.password })
        ).unwrap();

        if (response.user && response.token) {
          toast.success("OTP Verified. Logged in!");
          router("/dashboard");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
    }
  };


  async function sendOneTimePassword() {
    const email = getValues("username");
    const isValidEmail = await trigger("username");

    if (!(!isTimerRunning && isValidEmail)) return;

    try {
      await dispatch(loginWithOtp({ username: email })).unwrap();

      // Don't check response - loginWithOtp only sends OTP, doesn't return user/token

      let waitDuration = 30;
      start(waitDuration);
      toast.success(
        `An OTP has been sent to your email. Please use it to log in.`
      );
    } catch (error) {
      console.error("Error wile Sending OTP", error);
    }
  }

  useEffect(() => {
    if (emailFromParams) {
      setValue("username", emailFromParams);
    }
  }, [emailFromParams, setValue]);
  useEffect(() => {
    const handleOtpFlow = async () => {
      if (strategy === "otp") {
        const isValid = await trigger("username");

        if (!isValid) {
          setFocus("username");
          setValue("strategy", "password");
          return;
        }

        await sendOneTimePassword();
      }
    };

    handleOtpFlow();
  }, [strategy]);

  return (
    <>
      <div className="h-full m-auto flex justify-center items-center">
        <div className="w-auto ">
          {error && (
            <div className="border-red-700  border-[2px] font-bold text-xs bg-red-300 p-1 rounded text-red-700 my-2">
              Error : {error}
            </div>
          )}
          <div className="mb-6 flex justify-center">
            <img width={252} height={64} src={Icon.secondLogo} alt="Icon Image" />
          </div>
          <h2 className="flex text-[#5F488A] mb-5 text-xl font-normal font-lato tracking-normal mobile:justify-center">
            LOGIN
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col items-start">
              <input
                className="h-10 w-full rounded-[8px] bg-white shadow-[0_0_8px_2px_rgba(95,72,138,0.16)] text-base outline-none px-6 mobile:w-full mobile:h-[32px]"
                type="text"
                id="username"
                placeholder="Enter Email ID"
                {...register("username")}
                disabled={!!emailFromParams}
              />
              {errors.username?.message && (
                <ErrorMessage message={errors.username.message}></ErrorMessage>
              )}
            </div>
            <div className="flex flex-row justify-start gap-6 mobile:gap-4 items-center mobile:text-center mobile:justify-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  id="password"
                  value="password"
                  className="accent-[#5F488A]"
                  {...register("strategy")}
                />
                <span className="text-xs text-[#5F488A] font-bold whitespace-nowrap ">
                  With Password
                </span>
              </label>

              {/* OTP */}
              <label className="flex items-center gap-2 cursor-pointer ">
                <input
                  type="radio"
                  id="otp"
                  value="otp"
                  className="accent-[#5F488A]"
                  {...register("strategy")}
                // onChange={async () => {
                //   const isValid = await trigger("username");
                //   if (!isValid) {
                //     setFocus("username");
                //     setValue("strategy", "password");
                //     return;
                //   }

                //   await sendOneTimePassword();
                // }}
                />

                <span className="text-xs text-[#5F488A] font-bold whitespace-nowrap">
                  With OTP on Email
                </span>
              </label>
            </div>

            <div className="flex flex-col ">
              <div className="relative">
                <input
                  placeholder={
                    strategy === "password"
                      ? "Enter Password"
                      : "Enter OTP received on your registered email ID"
                  }
                  {...register("password")}
                  className="h-10 w-full rounded-[8px] bg-white shadow-[0_0_8px_2px_rgba(95,72,138,0.16)] text-base outline-none px-6 mobile:w-full mobile:h-[32px]"
                  type={showPassword ? "text" : "password"}
                />
                <span
                  className="absolute right-2 top-[15%]"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? (
                    <RiEyeFill color="#D4D6DB" size={25} />
                  ) : (
                    <RiEyeOffFill color="#D4D6DB" size={25} />
                  )}
                </span>
              </div>

              {errors.password?.message && (
                <ErrorMessage message={errors.password.message}></ErrorMessage>
              )}
            </div>
            {strategy === "password" && (
              <div className="text-start w-full text-lge desktop:text-sm largescreen:text-lg underline mobile:text-right mobile:font-lato mobile:text-[14px] mobile:mt-1 mobile:font-bold">
                <NavLink
                  className="font-normal text-[#545455] ml-1"
                  to={"/forgot-password"}
                >
                  Forgot your Password?
                </NavLink>
              </div>
            )}
            {strategy === "otp" && (
              <div className="text-start text-base desktop:text-sm largescreen:text-xl underline font-semibold text-app-blue ml-1">
                <button
                  disabled={isTimerRunning}
                  className={`font-normal ${isTimerRunning
                    ? "text-app-gray"
                    : "text-app-blue hover:underline"
                    }`}
                  onClick={() => sendOneTimePassword()}
                >
                  Resend OTP{" "}
                  {isTimerRunning && `in ${formatTime(timeLeft)} seconds`}
                </button>
              </div>
            )}
            <div className="">
              <button
                className="flex justify-Center bg-[#3B406E] shadow text-white text-lg h-full enabled:hover:scale-105 enabled:from-app-blue enabled:to-app-purple w-3/4 py-2 rounded-full desktop:text-lg largescreen:text-3xl disabled:bg-gray-500 disabled:cursor-wait  mobile:items-center mobile:flex-col mobile:w-[150px] mobile:h-[40px] mobile:jutify-center
                         mobile:mx-auto"
                type="submit"
                disabled={isLoading}
              >
                <span className="flex mx-auto my-auto mobile:items-center mobile:flex-col ">
                  Login
                  {isLoading && <Loader size={30} />}
                </span>
              </button>
            </div>
            <p className="mt-2 text-[#5F488A] flex items-center gap-3 flex-wrap">
              Don't have an account?
              <br className="mobile:block hidden" />
              <span
                className="underline cursor-pointer hover:text-[#4B356C] font-bold mobile:block "
                onClick={() => router("/signup")}
              >
                Sign Up
              </span>
            </p>
          </form>

          <div className="flex items-center gap-2 mt-2 px-2 w-auto h-[44px] rounded-lg bg-white shadow-[0_0_2px_1px_rgba(0,0,0,0.25)] 
            overflow-hidden mobile:flex mobile:h-[48px] mobile:mt-4 mobile:py-2 mobile:w-[266px] mobile:mx-auto mobile:justify-center mobile:item-start mobile:flex-col" >

            <button
              type="button"
              className="flex items-center justify-center gap-1 text-[#3B406E] font-bold text-[14px] w-[162px] max-w-auto h-[44px] rounded-lg bg-[#E9BE50] whitespace-nowrap p-3
              relative -ml-3 mobile:w-[286px] mobile:-mt-1 mobile:ml-0  mobile:h-[30px]">

              <img src={"img/SupportAgent.svg"} alt="support" className="w-5 h-5" />
              Support Center
            </button>

            <div className="w-auto gap-2 flex flex-row mobile:-mt-1">
              <a
                href="https://mail.google.com/mail/?view=cm&fs=1&to=support@viliyo.com&su=Support%20Request&body=Hello%20Team,"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1 text-[#3B406E] text-[12px] font-bold whitespace-nowrap mx-auto mobile:w-full mobile:justify-start"
              >
                <img src={"img/attach_email.svg"} alt="email" className="w-5 h-5" />
                support@viliyo.com
              </a>

              <a
                href="tel:+918976724006"
                className="flex items-center text-[#3B406E] text-[12px] font-bold whitespace-nowrap mobile:w-full mobile:justify-end"
              >
                <img src={"img/call.svg"} alt="phone" className="w-5 h-5" />
                +91 89767 24006
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginForm;